import clientApi, { handleETagError } from 'clientApi';
import { showToast, showErrorToast } from 'toast';
import { handleError, nowLoading, getRO } from 'redux/actions/dashboard/index';
import { recordCustomEvent } from 'monitoring/initNewRelic';
import { getDealerSubdomain } from 'utility/helpers/getDealerSubdomain';

export const requestAppraisal = (repairOrderId) => async (dispatch) => {
  dispatch(nowLoading());

  try {
    const response = await clientApi.requestAppraisal(repairOrderId);

    if (response.status === 200) {
      dispatch({ type: 'APPRAISAL_REQUESTED' });
      showToast('Thank you! Your request for appraisal has been submitted.');
    }
  } catch (err) {
    dispatch(handleError(err));
  }
};

export const submitRating = (repairOrderReview, rating) => async (dispatch) => {
  dispatch(nowLoading());

  try {
    const response = await clientApi.submitRating(repairOrderReview.id, rating);

    if (response.status === 200) {
      dispatch({ type: 'RATING_SUBMITTED' });
      dispatch(getRO());

      showToast('Thank you! Your rating has been submitted.');
    } else {
      throw new Error(
        'Unable to send rating. We greatly value your feedback; please check your internet connection and try again, or contact your service advisor to let us know how we did.',
      );
    }
  } catch (err) {
    dispatch(handleError(err));
  }
};
// Mirrors Quote.tsx's formatRecommendationTotal so recorded dollars match what the
// customer actually saw: compliance dealers are shown the tax-inclusive customerTotal,
// everyone else the parts+labor subtotal. NO CHARGE lines display as $0 and contribute
// nothing, even though their underlying totals are non-zero.
const resolveRecommendationAmount = (rec, recTotals, isComplianceDealer) => {
  if (rec.noCharge) {
    return 0;
  }

  if (isComplianceDealer) {
    return recTotals?.customerTotal ?? 0;
  }

  return (recTotals?.labor ?? 0) + (recTotals?.parts ?? 0) - (recTotals?.discountAmount ?? 0);
};

// Money accumulated as floats drifts (140.29000000000002); NR gets clean cents.
const toCents = (amount) => Math.round(amount * 100) / 100;

const recordQuoteSubmissionEvent = (recommendations, getState) => {
  const state = getState();
  const repairOrderQuote = state.dashboard.repairOrderQuote;
  const isComplianceDealer = state.dashboard.settings?.additionalQuoteCompliance === '1';
  const totalsById = (repairOrderQuote?.totals?.all?.recommendations ?? []).reduce(
    (acc, recTotals) => ({ ...acc, [recTotals.id]: recTotals }),
    {},
  );

  let approvedCount = 0;
  let approvedTotal = 0;
  let declinedCount = 0;
  let declinedTotal = 0;
  let preApprovedCount = 0;
  let preApprovedTotal = 0;

  recommendations.forEach((rec) => {
    const amount = resolveRecommendationAmount(rec, totalsById[rec.id], isComplianceDealer);

    if (rec.preApproved) {
      preApprovedCount += 1;
      preApprovedTotal += amount;
    } else if (rec.approved === true) {
      approvedCount += 1;
      approvedTotal += amount;
    } else if (rec.approved === false) {
      declinedCount += 1;
      declinedTotal += amount;
    }
  });

  recordCustomEvent('QuoteSubmission', {
    dealerSubdomain: getDealerSubdomain(),
    repairOrderId: repairOrderQuote?.repairOrderId,
    newExperienceEnabled: state.dashboard.settings?.estimateTapRedesign === '1',
    submissionDate: new Date().toISOString(),
    approvedCount,
    approvedTotal: toCents(approvedTotal),
    declinedCount,
    declinedTotal: toCents(declinedTotal),
    preApprovedCount,
    preApprovedTotal: toCents(preApprovedTotal),
  });
};

export const completeQuote =
  (quoteId, recommendations, eTag, signature) => async (dispatch, getState) => {
    dispatch(nowLoading());

    const customerResponses = recommendations
      .filter((rec) => !rec.preApproved)
      .map((rec) => {
        return {
          repairOrderQuoteRecommendationId: rec.id,
          approved: rec.approved === true,
        };
      });

    try {
      const response = await clientApi.completeQuote(
        quoteId,
        JSON.stringify(customerResponses),
        eTag,
        signature,
      );

      if (response.status === 200) {
        showToast('Thank you! Your quote responses have been submitted.');
        dispatch({ type: 'QUOTE_COMPLETED' });

        // Contained deliberately: the write already succeeded and the customer has already
        // been told so. A telemetry failure must never fall through to the catch below,
        // which would show an error toast and skip the getRO() refresh for a submission
        // that actually went through.
        try {
          recordQuoteSubmissionEvent(recommendations, getState);
        } catch (telemetryError) {
          console.error('Failed to record QuoteSubmission telemetry', telemetryError);
        }

        dispatch(getRO());
      } else {
        throw new Error(
          'Failed to submit quote; please try again or contact your service advisor.',
        );
      }
    } catch (err) {
      dispatch(handleETagError(err));
      // Explicit close, not a toggle: this catch can run with the quote already closed (a
      // throw from getRO() lands here after QUOTE_COMPLETED set isQuoteVisible false), and
      // a toggle would re-open it. The new experience is non-modal and never wants it set.
      dispatch({ type: 'CLOSE_QUOTE' });
      dispatch(handleError(err));
    }
  };
export const authorizeVisit = (signature) => async (dispatch, getState) => {
  dispatch(nowLoading());

  const roId = getState().dashboard.repairOrder?.id;

  try {
    const ackRes = await clientApi.acknowledgeWaiver(roId);
    if (ackRes.status !== 200) {
      throw new Error('Authorization failed. Please try again, or contact your service advisor.');
    }

    const sigRes = await clientApi.authorizeVisit(signature, roId);
    if (sigRes.status !== 200) {
      throw new Error('Authorization failed. Please try again, or contact your service advisor.');
    }

    dispatch({ type: 'VISIT_AUTHORIZED' });
    showToast('Thank you! Your visit has been authorized');
  } catch (err) {
    handleError(err);
  }
};

export const sendReceipts = (payments, email) => async (dispatch) => {
  dispatch({ type: 'SEND_RECEIPTS' });

  const promises = payments
    .filter((payment) => !!payment.transactionId)
    .map((payment) => clientApi.sendReceipt(payment.id, email));
  const response = await Promise.all(promises);
  if (response.every((res) => res.status === 200)) {
    return showToast('Successfully sent receipts!');
  }
  return showErrorToast({ message: 'One or more receipts failed to send!' });
};
export const financeRequested = () => (dispatch) => {
  dispatch({ type: 'FINANCE_REQUESTED' });

  showToast('Thank you! Your request for finance has been submitted.');
};
